import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { QrCode, Download, Copy, Trash2, Smartphone, Tablet, Monitor, ChevronDown, ChevronUp, ShoppingBag, RefreshCw, Printer, X, ZoomIn, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";

const fmtDuration = (s) => {
  if (!s) return "0 s";
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m} min ${r} s` : `${m} min`;
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

const DeviceIcon = ({ device }) => {
  if (device === "téléphone") return <Smartphone size={14} />;
  if (device === "tablette") return <Tablet size={14} />;
  return <Monitor size={14} />;
};

// Onglet admin « QR Codes » : création de QR codes traçables, téléchargement,
// et analyse complète des visiteurs qui les scannent (pages, durée, appareil, commande).
export function AdminQrCodes() {
  const [codes, setCodes] = useState(null);
  const [label, setLabel] = useState("");
  const [target, setTarget] = useState("/");
  const [producers, setProducers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [sessions, setSessions] = useState({});
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [viewQr, setViewQr] = useState(null);
  const [printingQr, setPrintingQr] = useState(null);

  const printQr = (qr) => {
    setPrintingQr(qr);
    document.body.classList.add("printing-qr");
    setTimeout(() => {
      const cleanup = () => {
        document.body.classList.remove("printing-qr");
        setPrintingQr(null);
        window.removeEventListener("afterprint", cleanup);
      };
      window.addEventListener("afterprint", cleanup);
      window.print();
    }, 120);
  };

  const load = async () => {
    try {
      const res = await api.get("/admin/qrcodes");
      setCodes(res.data);
    } catch {
      toast.error("Impossible de charger les QR codes");
    }
  };

  useEffect(() => {
    load();
    api
      .get("/wines")
      .then((res) => setProducers([...new Set(res.data.map((w) => w.producer).filter(Boolean))]))
      .catch(() => {});
  }, []);

  const create = async () => {
    if (!label.trim()) {
      toast.error("Donnez un nom à ce QR code (ex. « Tables salle », « Flyer Billecart »)");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/qrcodes", { label: label.trim(), target });
      setLabel("");
      toast.success("QR code créé — il est prêt à être téléchargé");
      load();
    } catch (err) {
      toast.error("Création impossible");
    } finally {
      setCreating(false);
    }
  };

  const download = async (qr) => {
    const res = await fetch(`${API}/qr/${qr.code}/image.png`);
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `qrcode-${qr.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("QR code téléchargé (PNG — prêt à imprimer)");
  };

  const downloadAffiche = async (qr) => {
    toast.info("Génération de l'affiche…");
    const res = await fetch(`${API}/qr/${qr.code}/affiche.png`);
    if (!res.ok) {
      toast.error("Affiche indisponible");
      return;
    }
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `affiche-${qr.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Affiche téléchargée (A5 haute définition — prête à imprimer et à partager)");
  };

  const copyLink = (qr) => {
    const url = `${window.location.origin}/api/qr/${qr.code}`;
    navigator.clipboard
      .writeText(url)
      .then(() => toast.success("Lien traçable copié"))
      .catch(() => toast.error(url));
  };

  const toggleSessions = async (qr) => {
    if (openId === qr.id) {
      setOpenId(null);
      return;
    }
    setOpenId(qr.id);
    if (sessions[qr.id]) return;
    setLoadingSessions(true);
    try {
      const res = await api.get(`/admin/qrcodes/${qr.id}/sessions`);
      setSessions((s) => ({ ...s, [qr.id]: res.data }));
    } catch {
      toast.error("Parcours indisponibles");
    } finally {
      setLoadingSessions(false);
    }
  };

  const remove = async (qr) => {
    if (!window.confirm(`Supprimer le QR code « ${qr.label} » et tout son historique ?`)) return;
    await api.delete(`/admin/qrcodes/${qr.id}`);
    toast.success("QR code supprimé");
    load();
  };

  const totals = useMemo(() => {
    if (!codes) return null;
    return {
      scans: codes.reduce((a, c) => a + (c.scans || 0), 0),
      conversions: codes.reduce((a, c) => a + (c.conversions || 0), 0),
    };
  }, [codes]);

  return (
    <div className="mt-8 space-y-5" data-testid="admin-qrcodes-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold text-[#1C1917]">QR codes traçables</h2>
          <p className="text-xs text-[#57534E] mt-1">
            Chaque scan est suivi : pages visitées, temps passé, appareil, et commande éventuelle.
          </p>
        </div>
        <button onClick={load} className="rounded-full border border-[#6B1226]/20 p-2.5 text-[#6B1226] hover:bg-[#6B1226]/5" data-testid="qr-refresh-button" aria-label="Actualiser">
          <RefreshCw size={15} />
        </button>
      </div>

      {totals && (
        <div className="grid grid-cols-3 gap-2.5" data-testid="qr-totals">
          {[
            ["Scans totaux", totals.scans],
            ["Commandes issues d'un QR", totals.conversions],
            ["QR codes actifs", codes.length],
          ].map(([k, v]) => (
            <div key={k} className="bg-white rounded-2xl border border-[#6B1226]/10 p-3.5 text-center">
              <p className="font-display text-2xl font-semibold text-[#6B1226]">{v}</p>
              <p className="text-[0.65rem] text-[#57534E] mt-1 leading-tight">{k}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[#6B1226]/10 p-5 space-y-3">
        <p className="eyebrow">Nouveau QR code</p>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Nom (ex. Tables salle, Flyer Billecart-Salmon…)"
          className="w-full rounded-xl border border-[#6B1226]/15 bg-[#FEF9F6] px-4 py-3 text-sm"
          data-testid="qr-label-input"
        />
        <select
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          className="w-full rounded-xl border border-[#6B1226]/15 bg-[#FEF9F6] px-4 py-3 text-sm"
          data-testid="qr-target-select"
        >
          <option value="/">Accueil du site</option>
          <option value="/vins">La cave (tous les vins)</option>
          <option value="/vins?cat=champagne">La cave — Champagnes</option>
          <option value="/vins?cat=rouge">La cave — Vins rouges</option>
          <option value="/vins?cat=blanc">La cave — Vins blancs</option>
          {producers.map((p) => (
            <option key={p} value={`/vins?producer=${encodeURIComponent(p)}`}>
              Sélection {p}
            </option>
          ))}
        </select>
        <button
          onClick={create}
          disabled={creating}
          className="w-full rounded-full bg-[#6B1226] text-[#FAF6F0] py-3 text-sm font-semibold tracking-wide hover:bg-[#4A0D1B] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="qr-create-button"
        >
          <QrCode size={16} /> {creating ? "Création…" : "Générer le QR code"}
        </button>
      </div>

      {codes === null ? (
        <p className="text-sm text-[#57534E] italic">Chargement…</p>
      ) : codes.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#6B1226]/10 p-10 text-center" data-testid="qr-empty">
          <p className="font-display text-xl text-[#1C1917]">Aucun QR code</p>
          <p className="text-sm text-[#57534E] mt-1.5">Créez-en un ci-dessus, imprimez-le, et suivez vos visiteurs.</p>
        </div>
      ) : (
        <ul className="space-y-4" data-testid="qr-list">
          {codes.map((qr) => (
            <li key={qr.id} className="bg-white rounded-2xl border border-[#6B1226]/10 p-5" data-testid="qr-card">
              <div className="flex flex-wrap items-start gap-4">
                <button
                  onClick={() => setViewQr(qr)}
                  className="relative shrink-0 rounded-xl border border-[#6B1226]/10 overflow-hidden group/qr cursor-zoom-in"
                  title="Cliquez pour agrandir"
                  data-testid="qr-view-button"
                >
                  <img
                    src={`${API}/qr/${qr.code}/image.png`}
                    alt={`QR code ${qr.label}`}
                    className="w-24 h-24 no-frame"
                    data-testid="qr-image"
                  />
                  <span className="absolute inset-0 hidden group-hover/qr:flex items-center justify-center bg-[#1C1917]/35 text-[#FAF6F0]">
                    <ZoomIn size={20} />
                  </span>
                </button>
                <div className="flex-1 min-w-[200px]">
                  <p className="font-semibold text-[#1C1917]">{qr.label}</p>
                  <p className="text-xs text-[#57534E] mt-0.5">Cible : {qr.target}</p>
                  <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid="qr-stats">
                    <span className="rounded-full bg-[#6B1226]/8 text-[#6B1226] px-2.5 py-1 text-[0.68rem] font-semibold">{qr.scans} scan{qr.scans > 1 ? "s" : ""}</span>
                    <span className="rounded-full bg-[#C5A059]/15 text-[#8C6D23] px-2.5 py-1 text-[0.68rem] font-semibold">durée moy. {fmtDuration(qr.avg_duration_s)}</span>
                    <span className="rounded-full bg-[#C5A059]/15 text-[#8C6D23] px-2.5 py-1 text-[0.68rem] font-semibold">{qr.avg_pages} pages/visite</span>
                    <span className="rounded-full bg-emerald-700/10 text-emerald-800 px-2.5 py-1 text-[0.68rem] font-semibold">{qr.conversions} commande{qr.conversions > 1 ? "s" : ""}</span>
                    <span className="rounded-full bg-[#1C1917]/5 text-[#57534E] px-2.5 py-1 text-[0.68rem]">dernier scan : {fmtDate(qr.last_scan)}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3.5">
                    <button
                      onClick={() => download(qr)}
                      className="rounded-full bg-[#6B1226] text-[#FAF6F0] px-4 py-2 text-xs font-semibold flex items-center gap-1.5 hover:bg-[#4A0D1B]"
                      data-testid="qr-download-button"
                    >
                      <Download size={13} /> Télécharger
                    </button>
                    <button
                      onClick={() => downloadAffiche(qr)}
                      className="rounded-full border border-[#C5A059]/50 text-[#8C6D23] px-4 py-2 text-xs font-semibold flex items-center gap-1.5 hover:bg-[#C5A059]/10"
                      data-testid="qr-affiche-button"
                    >
                      <Megaphone size={13} /> Affiche
                    </button>
                    <button
                      onClick={() => copyLink(qr)}
                      className="rounded-full border border-[#6B1226]/25 text-[#6B1226] px-4 py-2 text-xs font-semibold flex items-center gap-1.5 hover:bg-[#6B1226]/5"
                      data-testid="qr-copy-button"
                    >
                      <Copy size={13} /> Copier le lien
                    </button>
                    <button
                      onClick={() => toggleSessions(qr)}
                      className="rounded-full border border-[#6B1226]/25 text-[#6B1226] px-4 py-2 text-xs font-semibold flex items-center gap-1.5 hover:bg-[#6B1226]/5"
                      data-testid="qr-sessions-toggle"
                    >
                      {openId === qr.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Parcours ({qr.scans})
                    </button>
                    <button onClick={() => remove(qr)} className="rounded-full border border-red-800/25 text-red-800 px-3 py-2 text-xs hover:bg-red-800/5" data-testid="qr-delete-button" aria-label="Supprimer">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>

              {openId === qr.id && (
                <div className="mt-4 border-t border-[#6B1226]/10 pt-4" data-testid="qr-sessions">
                  {loadingSessions && !sessions[qr.id] ? (
                    <p className="text-sm text-[#57534E] italic">Chargement des parcours…</p>
                  ) : (sessions[qr.id] || []).length === 0 ? (
                    <p className="text-sm text-[#57534E] italic">Aucun scan pour le moment.</p>
                  ) : (
                    <ul className="space-y-2.5">
                      {(sessions[qr.id] || []).map((s) => (
                        <li key={s.id} className="rounded-xl bg-[#FEF9F6] border border-[#6B1226]/8 p-3.5" data-testid="qr-session-row">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#57534E]">
                            <span className="flex items-center gap-1 font-semibold text-[#1C1917]">
                              <DeviceIcon device={s.device} /> {s.device}
                            </span>
                            <span>{fmtDate(s.started_at)}</span>
                            <span>durée : <strong className="text-[#6B1226]">{fmtDuration(s.duration_s)}</strong></span>
                            <span>{s.pageviews} page{s.pageviews > 1 ? "s" : ""} vue{s.pageviews > 1 ? "s" : ""}</span>
                            {s.converted && (
                              <span className="flex items-center gap-1 rounded-full bg-emerald-700/10 text-emerald-800 px-2 py-0.5 font-semibold">
                                <ShoppingBag size={11} /> Commande {s.order_number}
                              </span>
                            )}
                          </div>
                          {s.pages?.length > 0 && (
                            <p className="mt-1.5 text-[0.7rem] text-[#57534E] leading-relaxed">
                              {s.pages.map((p, i) => (
                                <span key={i}>
                                  {i > 0 && <span className="text-[#C5A059] mx-1">→</span>}
                                  <span className="text-[#1C1917]">{p.path}</span>
                                  <span className="text-[#A8A29E]"> ({new Date(p.at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })})</span>
                                </span>
                              ))}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Agrandissement : le QR en grand, prêt à montrer ou à imprimer */}
      <AnimatePresence>
        {viewQr && (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="qr-view-modal"
          >
            <div className="absolute inset-0 bg-[#1C1917]/55 backdrop-blur-sm" onClick={() => setViewQr(null)} />
            <motion.div
              className="relative bg-[#FAF6F0] rounded-3xl p-6 sm:p-8 max-w-sm w-full text-center shadow-2xl"
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <button onClick={() => setViewQr(null)} className="absolute top-4 right-4 text-[#57534E] hover:text-[#6B1226]" data-testid="qr-view-close" aria-label="Fermer">
                <X size={18} />
              </button>
              <p className="eyebrow">QR code traçable</p>
              <p className="font-display text-xl font-semibold text-[#1C1917] mt-1">{viewQr.label}</p>
              <img
                src={`${API}/qr/${viewQr.code}/image.png`}
                alt={`QR code ${viewQr.label}`}
                className="w-64 h-64 sm:w-72 sm:h-72 mx-auto mt-4 no-frame"
                data-testid="qr-view-image"
              />
              <p className="text-[0.7rem] text-[#57534E] mt-2">Cible : {viewQr.target} — chaque scan est enregistré</p>
              <div className="flex justify-center gap-2.5 mt-5">
                <button
                  onClick={() => printQr(viewQr)}
                  className="rounded-full bg-[#6B1226] text-[#FAF6F0] px-5 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-[#4A0D1B]"
                  data-testid="qr-print-button"
                >
                  <Printer size={15} /> Imprimer
                </button>
                <button
                  onClick={() => download(viewQr)}
                  className="rounded-full border border-[#6B1226]/25 text-[#6B1226] px-5 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-[#6B1226]/5"
                  data-testid="qr-view-download"
                >
                  <Download size={15} /> Télécharger
                </button>
                <button
                  onClick={() => downloadAffiche(viewQr)}
                  className="rounded-full border border-[#C5A059]/50 text-[#8C6D23] px-5 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-[#C5A059]/10"
                  data-testid="qr-view-affiche"
                >
                  <Megaphone size={15} /> Affiche
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Feuille d'impression : seule elle reste visible à l'impression */}
      {printingQr &&
        createPortal(
          <div className="qr-print-sheet" data-testid="qr-print-sheet">
            <p className="qr-print-brand">LES VINS DU BISTROQUET</p>
            <p className="qr-print-label">{printingQr.label}</p>
            <img src={`${API}/qr/${printingQr.code}/image.png`} alt="" />
            <p className="qr-print-hint">Scannez pour découvrir la cave et commander vos vins</p>
            <p className="qr-print-url">www.lesvinsdubistroquet.com</p>
          </div>,
          document.body
        )}
    </div>
  );
}
